import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, ChevronDown, ChevronRight, Camera, Ruler, FileText, Sparkles, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getQuote, updateQuote, getConfig, uploadMedia, getMediaUrl, addSupplier } from "@/lib/api";
import api from "@/lib/api";
import { formatCurrency, computeActionRate, loadPricingSettings, getPricing, CALL_OUT_RATE, HOURS_PER_DAY } from "@/lib/calculations";
import { SupplierSelect } from "@/components/custom/SupplierSelect";

export default function MobileQuoteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState([]);
  const [config, setConfig] = useState({});
  const [expandedService, setExpandedService] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showParking, setShowParking] = useState(false);
  const [showQuoteInfo, setShowQuoteInfo] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadQuote = useCallback(async () => {
    try {
      const data = await getQuote(id);
      const withUids = {
        ...data,
        actions: (data.actions || []).map(a => (a && typeof a === "object" && !a._uid) ? { ...a, _uid: `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` } : a),
        measurements: (data.measurements || []).map(m => m && !m._uid ? { ...m, _uid: `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` } : m),
      };
      setDraft(withUids);
      setItems(data.items || []);
      setMedia(data.media || []);
      await loadPricingSettings();
    } catch { navigate("/mobile/today"); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => {
    loadQuote();
    getConfig().then(setConfig).catch(() => {});
  }, [loadQuote]);

  const updateDraft = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const updateAction = (idx, field, value) => {
    setDraft(prev => {
      const a = [...(prev.actions || [])];
      a[idx] = { ...a[idx], [field]: value };
      return { ...prev, actions: a };
    });
  };

  const addService = () => {
    setDraft(prev => {
      const a = [...(prev.actions || []), { _uid: `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, title: "", description: "", hours: 0, contractors: 1, price: 0, is_sub: false }];
      setExpandedService(a.length - 1);
      return { ...prev, actions: a };
    });
  };

  const [confirmRemoveServiceIdx, setConfirmRemoveServiceIdx] = useState(null);

  const requestRemoveService = (idx) => {
    const itemCount = items.filter(it => it.action_index === idx).length;
    if (itemCount === 0) {
      removeService(idx);
      return;
    }
    setConfirmRemoveServiceIdx(idx);
  };

  const confirmRemoveService = (idx) => {
    // Snapshot for undo
    const removedAction = draft.actions[idx];
    const removedItems = items.filter(it => it.action_index === idx).map(it => ({ ...it }));
    const shiftedSnapshot = items.filter(it => it.action_index > idx).map(it => ({ ...it }));
    // Compute post-remove actions array NOW so the toast undo closure doesn't capture a stale draft.actions
    const postRemoveActions = (draft.actions || []).filter((_, i) => i !== idx);
    setConfirmRemoveServiceIdx(null);
    removeService(idx);
    if (removedItems.length === 0) return;
    toast(
      `Service removed — “${removedAction?.title || "Service"}” and ${removedItems.length} material item${removedItems.length === 1 ? "" : "s"} deleted.`,
      {
        duration: 15000,
        action: {
          label: "Undo",
          onClick: () => {
            // Use postRemoveActions as the base so React closure capture order can't poison the restore
            setDraft(prev => {
              const base = Array.isArray(postRemoveActions) ? [...postRemoveActions] : [...(prev.actions || [])];
              const insertAt = Math.min(Math.max(idx, 0), base.length);
              base.splice(insertAt, 0, removedAction);
              return { ...prev, actions: base };
            });
            setItems(prev => {
              const shiftedIds = new Set(shiftedSnapshot.map(s => s.id));
              const reset = prev.map(it => shiftedIds.has(it.id)
                ? { ...it, action_index: (shiftedSnapshot.find(s => s.id === it.id) || { action_index: it.action_index }).action_index }
                : it);
              return [...reset, ...removedItems];
            });
            toast.success("Service restored");
          },
        },
      }
    );
  };

  const removeService = (idx) => {
    setDraft(prev => {
      const a = [...(prev.actions || [])];
      // Remove sub-tasks too
      const toRemove = [idx];
      for (let i = idx + 1; i < a.length; i++) { if (a[i]?.is_sub) toRemove.push(i); else break; }
      const filtered = a.filter((_, i) => !toRemove.includes(i));
      // Remove items and shift action_index down for remaining items
      setItems(prev => prev.filter(it => !toRemove.includes(it.action_index)).map(it => ({
        ...it,
        action_index: it.action_index > idx ? it.action_index - toRemove.length : it.action_index,
      })));
      return { ...prev, actions: filtered };
    });
    setExpandedService(null);
  };

  const addSubTask = (parentIdx) => {
    setDraft(prev => {
      const a = [...(prev.actions || [])];
      let insertAt = parentIdx + 1;
      while (insertAt < a.length && a[insertAt]?.is_sub) insertAt++;
      a.splice(insertAt, 0, { _uid: `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, description: "", is_sub: true });
      return { ...prev, actions: a };
    });
  };

  const handleAIGenerate = async () => {
    if (!draft.job_details?.trim()) { toast.error("Enter a job description first"); return; }
    setGenerating(true);
    try {
      const resp = await api.post("/ai/generate-quote", { job_description: draft.job_details, client_type: draft.client_type, address: draft.site_address });
      const result = resp.data;
      if (result.actions?.length > 0) {
        const aiActions = result.actions.map(a => (a && typeof a === "object" && !a._uid) ? { ...a, _uid: `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` } : a);
        setDraft(prev => ({ ...prev, actions: aiActions, job_details: result.job_details || prev.job_details }));
        if (result.items?.length > 0) setItems(result.items.map(it => ({ ...it, id: crypto.randomUUID(), quote_id: id })));
        toast.success("AI quote generated");
        setExpandedService(0);
      }
    } catch { toast.error("AI generation failed — try again"); } finally { setGenerating(false); }
  };

  // Per-scope memory of the last material added in this editing session. Same approach
  // as QuoteForm.jsx — store only the id, look up the live supplier from `items` at
  // add-time, so the next default tracks any in-place supplier edits on the most-
  // recently-added material but never moves when the user edits an earlier one.
  const lastAddedIdByScope = useRef({});
  const addItem = (actionIdx) => {
    const lastId = lastAddedIdByScope.current[actionIdx];
    const lastItem = lastId ? items.find(it => it.id === lastId) : null;
    const defaultSupplier = lastItem?.supplier || "";
    const newId = crypto.randomUUID();
    const newItem = { id: newId, quote_id: id, action_index: actionIdx, supplier: defaultSupplier, item_name: "", sku: "", unit_price: 0, qty: 1, location: "Click and Collect" };
    setItems(prev => [...prev, newItem]);
    lastAddedIdByScope.current[actionIdx] = newId;
  };

  const updateItem = (itemId, field, value) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, [field]: value } : it));
  };

  const removeItem = (itemId) => setItems(prev => prev.filter(it => it.id !== itemId));

  const handleSave = async () => {
    setSaving(true);
    try {
      const actionsWithPrices = (draft.actions || []).map(a => {
        if (typeof a !== "object" || a.is_sub) return a;
        if (a.price != null && a.price > 0) return a;
        return { ...a, price: computeActionRate(a.hours || 0, draft.client_type, draft.locked_hourly_rate) * (a.contractors || 1) };
      });
      const saveData = { ...draft, actions: actionsWithPrices, quote_started: true };
      if (draft.status === "Not Started") saveData.status = "Awaiting Quote";
      await updateQuote(id, saveData);
      // Save items
      for (const it of items) {
        try { await api.put(`/items/${it.id}`, it); } catch { await api.post("/items", it).catch(() => {}); }
      }
      toast.success("Quote saved");
      navigate(`/quotes/${id}`, { replace: true });
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  // Compute totals
  const totals = (() => {
    if (!draft) return { labour: 0, materials: 0, subtotal: 0, vat: 0, total: 0 };
    const actions = (draft.actions || []).filter(a => typeof a === "object" && !a.is_sub);
    const labour = actions.reduce((s, a) => s + (a.price || 0), 0);
    const materials = items.reduce((s, it) => s + (it.unit_price || 0) * (it.qty || 1), 0);
    const subtotal = labour + materials;
    const vat = subtotal * 0.2;
    return { labour, materials, subtotal, vat, total: subtotal + vat };
  })();

  if (loading || !draft) return <div className="flex items-center justify-center min-h-screen"><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  const mainActions = (draft.actions || []).filter(a => typeof a === "object" && !a.is_sub);
  const suppliers = [...new Set(["Toolstation", "Screwfix", "Wickes", "Travis Perkins", ...(config.suppliers || [])])].sort();

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <div className="sticky top-0 z-40 px-4 py-3 flex items-center justify-between" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm" style={{ color: "var(--text-secondary)" }}>
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Edit Quote</span>
        <Button onClick={handleSave} disabled={saving} size="sm" style={{ background: "#E05C2A", color: "#fff", borderRadius: "8px" }}>
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Site Details */}
        <div className="rounded-xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="badge-pill badge-blue text-xs">{draft.client_type}</span>
            {draft.works_type && <span className="badge-pill badge-warning text-xs">{draft.works_type}</span>}
          </div>
          <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{draft.site_address || "No address"}</p>
        </div>

        {/* Job Description */}
        <div className="rounded-xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <Label className="text-xs font-semibold uppercase mb-2 block" style={{ color: "var(--text-muted)" }}>Job Description</Label>
          <textarea
            value={draft.job_details || ""}
            onChange={(e) => updateDraft("job_details", e.target.value)}
            placeholder="Describe the work required..."
            className="w-full text-sm rounded-lg p-3 resize-y"
            style={{ border: "1px solid var(--border)", minHeight: "100px" }}
          />
        </div>

        {/* Services */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Services ({mainActions.length})</Label>
          </div>
          {mainActions.map((act, mi) => {
            const idx = (draft.actions || []).indexOf(act);
            const isExpanded = expandedService === idx;
            const serviceItems = items.filter(it => it.action_index === idx);
            const matTotal = serviceItems.reduce((s, it) => s + (it.unit_price || 0) * (it.qty || 1), 0);
            return (
              <div key={idx} className="rounded-xl mb-3 overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                {/* Header */}
                <button onClick={() => setExpandedService(isExpanded ? null : idx)} className="w-full flex items-center justify-between px-4 py-3 text-left" style={{ background: "#F3F4F6" }}>
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
                    <span className="text-sm font-semibold">{mi + 1}. {act.title || "Untitled"}</span>
                    {act.hours > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>{act.hours >= HOURS_PER_DAY ? `${Math.round(act.hours / HOURS_PER_DAY)}d` : `${act.hours}h`}</span>}
                  </div>
                  <span className="text-sm font-mono font-bold">{formatCurrency((act.price || 0) + matTotal)}</span>
                </button>
                {/* Body */}
                {isExpanded && (
                  <div className="px-4 py-3 space-y-3">
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Service Name</Label>
                      <Input value={act.title || ""} onChange={(e) => updateAction(idx, "title", e.target.value)} placeholder="e.g. Bathroom refurbishment" className="h-11 rounded-lg" style={{ border: "1px solid var(--border)", fontSize: "14px" }} />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Description</Label>
                      <textarea value={act.description || ""} onChange={(e) => updateAction(idx, "description", e.target.value)} placeholder="Describe the work..." className="w-full text-sm rounded-lg p-3 resize-y" style={{ border: "1px solid var(--border)", minHeight: "70px" }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Duration</Label>
                        <Select value={String(act.hours || 0)} onValueChange={(v) => {
                          if (v === "__custom__") {
                            const days = prompt("Enter number of days:");
                            if (days) { const h = parseFloat(days) * HOURS_PER_DAY; updateAction(idx, "hours", h); updateAction(idx, "price", computeActionRate(h, draft.client_type, draft.locked_hourly_rate) * (act.contractors || 1)); }
                          } else {
                            const h = parseFloat(v); updateAction(idx, "hours", h); updateAction(idx, "price", computeActionRate(h, draft.client_type, draft.locked_hourly_rate) * (act.contractors || 1));
                          }
                        }}>
                          <SelectTrigger className="h-11 rounded-lg" style={{ fontSize: "14px" }}><SelectValue placeholder="Duration" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">No duration</SelectItem>
                            {[1,2,3,4,5,6,7].map(h => <SelectItem key={h} value={String(h)}>{h} hrs</SelectItem>)}
                            {[8,16,24,32,40].map(h => <SelectItem key={h} value={String(h)}>{h/HOURS_PER_DAY} day{h>HOURS_PER_DAY?"s":""}</SelectItem>)}
                            <SelectItem value="__custom__">Custom days...</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Contractors</Label>
                        <Select value={String(act.contractors || 1)} onValueChange={(v) => { const n = parseInt(v); updateAction(idx, "contractors", n); updateAction(idx, "price", computeActionRate(act.hours || 0, draft.client_type, draft.locked_hourly_rate) * n); }}>
                          <SelectTrigger className="h-11 rounded-lg" style={{ fontSize: "14px" }}><SelectValue /></SelectTrigger>
                          <SelectContent>{[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n} {n===1?"person":"people"}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-2" style={{ borderTop: "1px solid var(--border)" }}>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>Labour total</span>
                      <span className="text-sm font-mono font-bold">{formatCurrency(act.price || 0)}</span>
                    </div>

                    {/* Materials */}
                    {serviceItems.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Materials ({serviceItems.length})</Label>
                        {serviceItems.map(it => (
                          <div key={it.id} className="p-3 rounded-lg space-y-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                            <SupplierSelect
                              value={it.supplier}
                              suppliers={suppliers}
                              onChange={(v) => updateItem(it.id, "supplier", v)}
                              onAddSupplier={async (name) => {
                                const res = await addSupplier(name);
                                if (res?.suppliers) setConfig(prev => ({ ...prev, suppliers: res.suppliers }));
                              }}
                              triggerClassName="h-10 rounded-lg text-sm"
                              placeholder="Select supplier…"
                              testId={`mobile-supplier-${it.id}`}
                            />
                            <Input value={it.item_name || ""} onChange={(e) => updateItem(it.id, "item_name", e.target.value)} placeholder="Item name" className="h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
                            <div className="grid grid-cols-2 gap-2">
                              <Input type="number" step="0.01" value={it.unit_price || ""} onChange={(e) => updateItem(it.id, "unit_price", parseFloat(e.target.value) || 0)} placeholder="Price" className="h-10 rounded-lg text-sm font-mono" style={{ border: "1px solid var(--border)" }} />
                              <Input type="number" min="1" value={it.qty || ""} onChange={(e) => updateItem(it.id, "qty", parseInt(e.target.value) || 1)} placeholder="Qty" className="h-10 rounded-lg text-sm font-mono" style={{ border: "1px solid var(--border)" }} />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-mono font-bold">{formatCurrency((it.unit_price || 0) * (it.qty || 1))}</span>
                              <button onClick={() => removeItem(it.id)} style={{ color: "#DC2626" }}><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <button onClick={() => addItem(idx)} className="w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-1" style={{ border: "1px solid var(--primary)", color: "var(--primary)" }}>
                      <Plus className="w-4 h-4" /> Add Material
                    </button>

                    {/* Sub-tasks */}
                    {(draft.actions || []).map((sub, si) => {
                      if (!sub?.is_sub) return null;
                      // Check if this sub belongs to current service
                      let parentIdx = si - 1;
                      while (parentIdx >= 0 && (draft.actions[parentIdx]?.is_sub)) parentIdx--;
                      if (parentIdx !== idx) return null;
                      return (
                        <div key={sub._uid || `sub-${si}`} className="pl-3 py-2" style={{ borderLeft: "3px solid #E5E7EB" }}>
                          <div className="flex items-center gap-2">
                            <textarea value={sub.description || ""} onChange={(e) => updateAction(si, "description", e.target.value)} placeholder="Sub-task description..." className="flex-1 text-sm rounded-lg p-2 resize-y" style={{ border: "1px solid var(--border)", minHeight: "44px" }} />
                            <button onClick={() => { const a = [...(draft.actions || [])]; a.splice(si, 1); setDraft(p => ({ ...p, actions: a })); }} style={{ color: "#DC2626" }}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                      );
                    })}
                    <button onClick={() => addSubTask(idx)} className="w-full py-2 text-xs font-medium flex items-center justify-center gap-1" style={{ color: "var(--text-muted)" }}>
                      <Plus className="w-3 h-3" /> Add Sub-task
                    </button>

                    {confirmRemoveServiceIdx === idx ? (
                      <div className="rounded-lg p-3 mt-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }} data-testid={`mq-remove-confirm-${idx}`}>
                        <p className="text-xs mb-2" style={{ color: "#7F1D1D" }}>
                          Remove this service and its {items.filter(it => it.action_index === idx).length} material item{items.filter(it => it.action_index === idx).length === 1 ? "" : "s"}? This cannot be undone.
                        </p>
                        <div className="flex gap-2">
                          <button onClick={() => confirmRemoveService(idx)} className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: "#DC2626", color: "#fff" }} data-testid={`mq-confirm-remove-${idx}`}>Confirm Remove</button>
                          <button onClick={() => setConfirmRemoveServiceIdx(null)} className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid={`mq-cancel-remove-${idx}`}>Keep Service</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => requestRemoveService(idx)} className="w-full py-2 text-sm font-medium text-center" style={{ color: "#DC2626" }} data-testid={`mq-remove-service-${idx}`}>Remove Service</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={addService} className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1" style={{ border: "2px dashed var(--border)", color: "var(--primary)" }}>
            <Plus className="w-4 h-4" /> Add Service
          </button>
          {/* AI Generate */}
          <button onClick={handleAIGenerate} disabled={generating} className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 mt-2" style={{ background: generating ? "#9CA3AF" : "#7C3AED", color: "#fff", borderRadius: "12px" }}>
            <Sparkles className="w-4 h-4" /> {generating ? "Generating..." : "AI Generate Quote"}
          </button>
        </div>

        {/* Quote Info — collapsible */}
        <div className="rounded-xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <button onClick={() => setShowQuoteInfo(!showQuoteInfo)} className="w-full flex items-center justify-between px-4 py-3">
            <Label className="text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Quote Info</Label>
            {showQuoteInfo ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
          </button>
          {showQuoteInfo && (
            <div className="px-4 pb-3 space-y-1 text-sm">
              <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Client Type</span><span>{draft.client_type}</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Rate</span><span>{formatCurrency(draft.locked_hourly_rate || getPricing(draft.client_type).hourly_rate || 54)}/hr</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Services</span><span>{mainActions.length}</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Items</span><span>{items.length}</span></div>
              <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Photos</span><span>{(media || []).filter(m => m.category === "photo" && !m.is_deleted).length}</span></div>
            </div>
          )}
        </div>

        {/* Measurements — collapsible */}
        <div className="rounded-xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <button onClick={() => setShowMeasurements(!showMeasurements)} className="w-full flex items-center justify-between px-4 py-3">
            <Label className="text-xs font-semibold uppercase flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Ruler className="w-3.5 h-3.5" /> Measurements ({(draft.measurements || []).length})</Label>
            {showMeasurements ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
          </button>
          {showMeasurements && (
            <div className="px-4 pb-3 space-y-2">
              {(draft.measurements || []).map((m, mi) => (
                <div key={m._uid || `m-${mi}`} className="flex items-center gap-2">
                  <Input value={m.label || ""} onChange={(e) => { const ms = [...(draft.measurements || [])]; ms[mi] = { ...ms[mi], label: e.target.value }; updateDraft("measurements", ms); }} placeholder="Label" className="h-10 flex-1 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
                  <Input value={m.value || ""} onChange={(e) => { const ms = [...(draft.measurements || [])]; ms[mi] = { ...ms[mi], value: e.target.value }; updateDraft("measurements", ms); }} placeholder="Value" className="h-10 w-24 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
                  <button onClick={() => { const ms = [...(draft.measurements || [])]; ms.splice(mi, 1); updateDraft("measurements", ms); }} style={{ color: "#DC2626" }}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => updateDraft("measurements", [...(draft.measurements || []), { _uid: `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label: "", value: "", unit: "m" }])} className="w-full py-2 text-sm font-medium flex items-center justify-center gap-1" style={{ color: "var(--primary)" }}>
                <Plus className="w-3 h-3" /> Add Measurement
              </button>
            </div>
          )}
        </div>

        {/* Additional Terms — collapsible */}
        <div className="rounded-xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <button onClick={() => setShowTerms(!showTerms)} className="w-full flex items-center justify-between px-4 py-3">
            <Label className="text-xs font-semibold uppercase flex items-center gap-1" style={{ color: "var(--text-muted)" }}><FileText className="w-3.5 h-3.5" /> Additional Terms</Label>
            {showTerms ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
          </button>
          {showTerms && (
            <div className="px-4 pb-3">
              <textarea value={draft.additional_terms || ""} onChange={(e) => updateDraft("additional_terms", e.target.value)} placeholder="Additional terms and conditions..." className="w-full text-sm rounded-lg p-3 resize-y" style={{ border: "1px solid var(--border)", minHeight: "80px" }} />
            </div>
          )}
        </div>

        {/* Parking — collapsible */}
        <div className="rounded-xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <button onClick={() => setShowParking(!showParking)} className="w-full flex items-center justify-between px-4 py-3">
            <Label className="text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Parking {draft.parking_type ? `(${draft.parking_type})` : ""}</Label>
            {showParking ? <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
          </button>
          {showParking && (
            <div className="px-4 pb-3 space-y-3">
              <div>
                <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Parking Type</Label>
                <Select value={draft.parking_type || ""} onValueChange={(v) => updateDraft("parking_type", v)}>
                  <SelectTrigger className="h-11 rounded-lg" style={{ fontSize: "14px" }}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pay by Phone">Pay by Phone</SelectItem>
                    <SelectItem value="Residents Visitor Permit">Residents Visitor Permit</SelectItem>
                    <SelectItem value="Paid Car Park">Paid Car Park</SelectItem>
                    <SelectItem value="Concierge">Concierge</SelectItem>
                    <SelectItem value="Free on site/road">Free on site/road</SelectItem>
                    <SelectItem value="Dartford Crossing">Dartford Crossing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Hours</Label>
                  <Input type="number" value={draft.parking_hours || ""} onChange={(e) => updateDraft("parking_hours", parseFloat(e.target.value) || 0)} className="h-11 rounded-lg" style={{ border: "1px solid var(--border)" }} />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: "var(--text-muted)" }}>Rate (inc VAT)</Label>
                  <Input type="number" step="0.01" value={draft.parking_rate || ""} onChange={(e) => updateDraft("parking_rate", parseFloat(e.target.value) || 0)} className="h-11 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Congestion */}
        <div className="rounded-xl p-4 flex items-center justify-between" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <Label className="text-xs font-semibold uppercase" style={{ color: "var(--text-muted)" }}>Congestion Days</Label>
          <Input type="number" min="0" value={draft.congestion_days || ""} onChange={(e) => updateDraft("congestion_days", parseInt(e.target.value) || 0)} className="h-10 w-20 rounded-lg text-sm font-mono text-right" style={{ border: "1px solid var(--border)" }} />
        </div>

        {/* Job Photos */}
        <div className="rounded-xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <Label className="text-xs font-semibold uppercase mb-1 block flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
            <Camera className="w-3.5 h-3.5" /> Job Photos ({(media || []).filter(m => m.category === "photo" && !m.is_deleted).length})
          </Label>
          <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>Appear on the quote and invoice PDF.</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(media || []).filter(m => m.category === "photo" && !m.is_deleted).map(p => (
              <img key={p.id} src={getMediaUrl(p.storage_path)} alt="" className="w-full aspect-square object-cover rounded-lg" />
            ))}
          </div>
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer ${uploading ? "opacity-50" : ""}`} style={{ borderColor: "var(--border)" }}>
            <Camera className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{uploading ? "Uploading..." : "Add Job Photo"}</span>
            <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              setUploading(true);
              for (const f of files) {
                try { await uploadMedia(id, f, "photo"); }
                catch (err) { console.error("Photo upload failed", err); toast.error("Failed to upload photo"); }
              }
              loadQuote();
              setUploading(false);
              e.target.value = "";
            }} />
          </label>
        </div>

        {/* Material Purchases — separate, not in PDFs */}
        <div className="rounded-xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <Label className="text-xs font-semibold uppercase mb-1 block flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
            <FileText className="w-3.5 h-3.5" /> Material Purchases ({(media || []).filter(m => m.category === "receipt" && !m.is_deleted).length})
          </Label>
          <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>For your records only — not on PDFs.</p>
          {(media || []).filter(m => m.category === "receipt" && !m.is_deleted).length > 0 && (
            <div className="space-y-1.5 mb-3">
              {(media || []).filter(m => m.category === "receipt" && !m.is_deleted).map(r => (
                <div key={r.id} className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                  <a href={getMediaUrl(r.storage_path)} target="_blank" rel="noopener noreferrer" className="text-xs flex-1 truncate" style={{ color: "var(--primary)" }}>{r.original_filename}</a>
                </div>
              ))}
            </div>
          )}
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer ${uploading ? "opacity-50" : ""}`} style={{ borderColor: "var(--border)" }}>
            <Upload className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{uploading ? "Uploading..." : "Upload Receipt or Invoice"}</span>
            <input type="file" accept="image/*,.pdf" multiple className="hidden" disabled={uploading} onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              setUploading(true);
              for (const f of files) {
                try { await uploadMedia(id, f, "receipt"); }
                catch (err) { console.error("Receipt upload failed", err); toast.error("Failed to upload receipt"); }
              }
              loadQuote();
              setUploading(false);
              e.target.value = "";
            }} />
          </label>
        </div>

        {/* Documents — internal reference only, not on PDFs */}
        <div className="rounded-xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <Label className="text-xs font-semibold uppercase mb-1 block flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
            <FileText className="w-3.5 h-3.5" /> Documents ({(media || []).filter(m => m.category === "document" && !m.is_deleted).length})
          </Label>
          <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>Internal reference only — not on PDFs.</p>
          {(media || []).filter(m => m.category === "document" && !m.is_deleted).length > 0 && (
            <div className="space-y-1.5 mb-3">
              {(media || []).filter(m => m.category === "document" && !m.is_deleted).map(d => (
                <div key={d.id} className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                  <a href={getMediaUrl(d.storage_path)} target="_blank" rel="noopener noreferrer" className="text-xs flex-1 truncate" style={{ color: "var(--primary)" }}>{d.original_filename}</a>
                </div>
              ))}
            </div>
          )}
          <label className={`flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-xl cursor-pointer ${uploading ? "opacity-50" : ""}`} style={{ borderColor: "var(--border)" }}>
            <Upload className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{uploading ? "Uploading..." : "Upload Document"}</span>
            <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" multiple className="hidden" disabled={uploading} onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              setUploading(true);
              for (const f of files) {
                try { await uploadMedia(id, f, "document"); }
                catch (err) { console.error("Document upload failed", err); toast.error("Failed to upload document"); }
              }
              loadQuote();
              setUploading(false);
              e.target.value = "";
            }} />
          </label>
        </div>
      </div>

      {/* Financial Summary Sticky Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50" style={{ background: "var(--surface)", borderTop: "1px solid var(--border)" }}>
        {showSummary && (
          <div className="px-4 py-3 space-y-1 text-sm" style={{ borderTop: "1px solid var(--border)" }}>
            <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Labour</span><span className="font-mono">{formatCurrency(totals.labour)}</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Materials</span><span className="font-mono">{formatCurrency(totals.materials)}</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Subtotal Ex VAT</span><span className="font-mono">{formatCurrency(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>VAT (20%)</span><span className="font-mono">{formatCurrency(totals.vat)}</span></div>
          </div>
        )}
        <button onClick={() => setShowSummary(!showSummary)} className="w-full flex items-center justify-between px-4 py-3">
          <span className="text-base font-bold" style={{ color: "var(--text-primary)" }}>Total: {formatCurrency(totals.total)}</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>{showSummary ? "▼ Hide" : "▲ Details"}</span>
        </button>
      </div>
    </div>
  );
}
